/**
 * Web Vitals Reporting
 * Tracks and reports Core Web Vitals metrics for all pages
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB, Metric } from "web-vitals";

// Types for analytics
interface AnalyticsEvent {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  delta: number;
  id: string;
  navigationType: string;
  pathname: string;
  timestamp: number;
}

// Store metrics for display
const metricsStore: AnalyticsEvent[] = [];

// Get rating based on thresholds
function getRating(metric: Metric): "good" | "needs-improvement" | "poor" {
  const thresholds: Record<string, [number, number]> = {
    CLS: [0.1, 0.25],
    FCP: [1800, 3000],
    INP: [200, 500],
    LCP: [2500, 4000],
    TTFB: [800, 1800],
  };

  const [good, poor] = thresholds[metric.name] || [0, 0];

  if (metric.value <= good) return "good";
  if (metric.value <= poor) return "needs-improvement";
  return "poor";
}

// Format metric value for display
function formatValue(name: string, value: number): string {
  if (name === "CLS") {
    return value.toFixed(3);
  }
  return `${Math.round(value)}ms`;
}

// Send metric to analytics endpoint
function sendToAnalytics(metric: Metric) {
  const event: AnalyticsEvent = {
    name: metric.name,
    value: metric.value,
    rating: getRating(metric),
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType,
    pathname: window.location.pathname,
    timestamp: Date.now(),
  };

  // Store locally
  metricsStore.push(event);

  // Log to console in development
  if (process.env.NODE_ENV === "development") {
    const emoji =
      event.rating === "good"
        ? "✅"
        : event.rating === "needs-improvement"
          ? "⚠️"
          : "❌";
    console.log(
      `${emoji} ${event.name}: ${formatValue(event.name, event.value)} (${event.rating})`,
      {
        pathname: event.pathname,
        navigationType: event.navigationType,
        delta: event.delta,
      },
    );
  }

  // Send to analytics service (customize this)
  if (typeof window !== "undefined") {
    const payload = {
      value: Math.round(
        metric.name === "CLS" ? metric.value * 1000 : metric.value,
      ),
      metric_id: metric.id,
      metric_value: metric.value,
      metric_delta: metric.delta,
      metric_rating: event.rating,
      page_path: event.pathname,
    };

    if (window.gtag) {
      window.gtag("event", metric.name, payload);
    } else {
      // GTM only loads on the first user interaction (see the gtm-script in
      // app/layout.tsx), and web-vitals reports LCP/CLS at that same moment —
      // so `window.gtag` is usually still undefined here and every sample was
      // being dropped on the floor. dataLayer is a plain array that GTM
      // replays once it boots, so queueing there never loses a metric.
      // Requires a GTM trigger on the `web-vitals` custom event to reach GA4.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "web-vitals",
        metric_name: metric.name,
        ...payload,
      });
    }
  }

  // Send to custom endpoint (optional)
  if (process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT) {
    fetch(process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(event),
      keepalive: true,
    }).catch(console.error);
  }
}

// Initialize Web Vitals tracking
export function initWebVitals() {
  if (typeof window === "undefined") return;

  onCLS(sendToAnalytics);
  onFCP(sendToAnalytics);
  onINP(sendToAnalytics);
  onLCP(sendToAnalytics);
  onTTFB(sendToAnalytics);

  // Log initialization
  if (process.env.NODE_ENV === "development") {
    // console.log("📊 Web Vitals tracking initialized");
  }
}

// Get all stored metrics
export function getMetrics(): AnalyticsEvent[] {
  return [...metricsStore];
}

// Get metrics summary by page
export function getMetricsSummary() {
  const summary: Record<string, Record<string, AnalyticsEvent[]>> = {};

  metricsStore.forEach((metric) => {
    if (!summary[metric.pathname]) {
      summary[metric.pathname] = {};
    }
    if (!summary[metric.pathname][metric.name]) {
      summary[metric.pathname][metric.name] = [];
    }
    summary[metric.pathname][metric.name].push(metric);
  });

  return summary;
}

// Clear stored metrics
export function clearMetrics() {
  metricsStore.length = 0;
}

// Export for global access
if (typeof window !== "undefined") {
  (window as any).__webVitals = {
    getMetrics,
    getMetricsSummary,
    clearMetrics,
  };
}
